import { isFieldBinding } from "@redotech/effect-api-shared/default-rules/field-binding";
import { DefaultRuleFlowTemplate } from "@redotech/effect-api-shared/default-rules/types";
import {
  createContext,
  memo,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

/**
 * When present, signals that the surrounding UI is a default-rule edit
 * page: leaf editors should render inputs in a locked state UNLESS the
 * rule's template binds that specific field.
 *
 * Leaf editors query `isFieldEditable(templateNode, fieldName)` to
 * decide per-input. `findActionTemplate` / `findConditionTemplate`
 * locate the matching sub-template via identity (by actionType /
 * conditionType). Current rules only declare one action or condition
 * of each kind, which keeps identity-matching unambiguous; revisit if
 * we ever ship a rule with multiple ASSIGN_V3 actions, say.
 */
export interface DefaultRuleEditContextValue {
  readonly template: DefaultRuleFlowTemplate;
  readonly findActionTemplate: (
    actionType: string,
  ) => Record<string, unknown> | undefined;
  readonly findConditionTemplate: (
    conditionType: string,
  ) => Record<string, unknown> | undefined;
  readonly isFieldEditable: (
    nodeTemplate: Record<string, unknown> | undefined,
    fieldName: string,
  ) => boolean;
}

export const DefaultRuleEditContext =
  createContext<DefaultRuleEditContextValue | null>(null);

export function useDefaultRuleEditContext(): DefaultRuleEditContextValue | null {
  return useContext(DefaultRuleEditContext);
}

/**
 * Deep-walks the template and returns the first node matching `predicate`.
 * Used by the action/condition finders.
 */
function findFirst(
  node: unknown,
  predicate: (obj: Record<string, unknown>) => boolean,
): Record<string, unknown> | undefined {
  if (node !== null && typeof node === "object" && !Array.isArray(node)) {
    const obj = node as Record<string, unknown>;
    if (predicate(obj)) return obj;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      const match = findFirst(item, predicate);
      if (match) return match;
    }
    return undefined;
  }
  if (node !== null && typeof node === "object") {
    for (const value of Object.values(node as Record<string, unknown>)) {
      const match = findFirst(value, predicate);
      if (match) return match;
    }
  }
  return undefined;
}

export const DefaultRuleEditContextProvider = memo(
  function DefaultRuleEditContextProvider({
    template,
    children,
  }: {
    template: DefaultRuleFlowTemplate;
    children: ReactNode;
  }) {
    const value = useMemo<DefaultRuleEditContextValue>(() => {
      return {
        template,
        findActionTemplate: (actionType) =>
          findFirst(template, (obj) => obj.actionType === actionType),
        findConditionTemplate: (conditionType) =>
          findFirst(template, (obj) => obj.conditionType === conditionType),
        isFieldEditable: (nodeTemplate, fieldName) => {
          if (!nodeTemplate) return false;
          return isFieldBinding(nodeTemplate[fieldName]);
        },
      };
    }, [template]);

    return (
      <DefaultRuleEditContext.Provider value={value}>
        {children}
      </DefaultRuleEditContext.Provider>
    );
  },
);
